import React, {
  createContext, PropsWithChildren, useContext, useState, useCallback, useMemo,
} from 'react';

export const DEFAULT_CONTEXT_ID = 'default_adaptive_text_area_context';

interface AdaptiveTextAreaContextProps {
    minFontSizes: Map<string, number>;
    setMinFontSize: (contextId: string, size: number) => void;
}

const AdaptiveTextAreaContext = createContext<AdaptiveTextAreaContextProps | undefined>(undefined);

export const AdaptiveTextAreaProvider: React.FC<PropsWithChildren
> = ({ children }) => {
  const [ minFontSizes, setMinFontSizes ] = useState(
    new Map([ [ DEFAULT_CONTEXT_ID, 16 ] ])
  );

  const setMinFontSize = useCallback((contextId: string, size: number) => {
    setMinFontSizes((prevSizes) => {
      const currentSize = prevSizes.get(contextId);
      if (currentSize !== size) {
        const newSizes = new Map(prevSizes);
        newSizes.set(contextId, size);
        return newSizes;
      }
      return prevSizes;
    });
  }, []);

  const value = useMemo(() => ({ minFontSizes, setMinFontSize }), [ minFontSizes, setMinFontSize ]);

  return (
    <AdaptiveTextAreaContext.Provider value={value}>
      {children}
    </AdaptiveTextAreaContext.Provider>
  );
};

export const useAdaptiveTextAreaContext = () => {
  const context = useContext(AdaptiveTextAreaContext);
  if (!context) {
    throw new Error('useAdaptiveTextAreaContext must be used within an AdaptiveTextAreaProvider');
  }
  return context;
};
